/**
 * Screen 12's calls. Its own client rather than lines in `src/api.ts`, both
 * because the shared one has no DELETE and because a feature that owns its
 * fetches is a feature that can be deleted in one directory.
 */

import { request as shared } from "../api.js";
import type { GuestListScope, MfaRule, StandardPour, TabletPolicy } from "@baros/core";


export class OrgApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "OrgApiError";
  }
}

export async function request<T>(
  token: string,
  method: "GET" | "PUT" | "POST" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body, (status, message) => new OrgApiError(status, message));
}

const id = encodeURIComponent;

// --------------------------------------------------------------------- types

export interface OrgSettings {
  readonly orderLimitsCents: Readonly<Record<string, number>>;
  readonly wastageReasons: readonly string[];
  readonly prepTabs: readonly string[];
  /** How the bar writes a drink, for the development chat (#72). */
  readonly houseStyle?: string | undefined;
  /** The measures the bar pours to; the development chat proposes only these. */
  readonly standardPours?: readonly StandardPour[] | undefined;
  /** How the organisation's tablets lock and what they fence (#80). */
  readonly tabletPolicy?: TabletPolicy | undefined;
  /** What venue managers see of the guest list. */
  readonly guestListScope?: GuestListScope | undefined;
  /** Who has to use two-step sign-in (#92). */
  readonly mfaRule?: MfaRule | undefined;
  /** Written reports, off unless an owner turns them on (#169). */
  readonly aiReports?: boolean | undefined;
  /** Set by the subscription tier. Never editable here — D10. */
  readonly aiPlanCeilingCents: number;
  readonly aiMonthlyLimitCents: number;
}

export interface OrgVenue {
  readonly venueId: string;
  readonly name: string;
  readonly suburb: string;
  readonly state: string;
  readonly seats: number;
  /** Prices on the guest page (#126); on unless turned off. */
  readonly guestPrices?: boolean;
  /** The venue's clock: its timezone, the hour its day ends, the day its week starts. */
  readonly timezone?: string;
  readonly rolloverHour?: number;
  readonly weekStartsOn?: number;
  /** The gross profit a drink is held to, percent. */
  readonly gpTarget?: number;
  readonly staffCount: number;
}

export interface MemberVenue {
  readonly venueId: string;
  readonly name: string;
  readonly role: string | null;
}

export interface OrgMember {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly orgRole: string | null;
  /** True for the signed-in person — the screen warns before they change it. */
  readonly isYou: boolean;
  readonly venues: readonly MemberVenue[];
}

export interface OrgOverview {
  readonly org: {
    readonly orgId: string;
    readonly name: string;
    readonly settings: OrgSettings;
  };
  readonly venues: readonly OrgVenue[];
  readonly members: readonly OrgMember[];
}

/** One tab renamed in the same save — see `PrepTabRename` in org-validate.ts. */
export interface PrepTabRename {
  readonly from: string;
  readonly to: string;
}

export interface SettingsBody {
  readonly name: string;
  readonly orderLimitsCents: Readonly<Record<string, number>>;
  readonly wastageReasons: readonly string[];
  readonly prepTabs: readonly string[];
  readonly aiMonthlyLimitCents: number;
  /** Optional: tabs kept but renamed rather than dropped and re-added. */
  readonly prepTabRenames?: readonly PrepTabRename[];
  /** The development chat's house-style note (#72). Left out, it is kept. */
  readonly houseStyle?: string;
  /** The bar's measures. Left out, they are kept; an empty list switches the rule off. */
  readonly standardPours?: readonly StandardPour[];
  /** The tablet policy (#80). Left out, it is kept. */
  readonly tabletPolicy?: TabletPolicy;
  /** What venue managers see of the guest list. Left out, it is kept. */
  readonly guestListScope?: GuestListScope;
  /** Who has to use two-step sign-in (#92), the owner's to change. Left out, it is kept. */
  readonly mfaRule?: MfaRule;
}

/** How many tasks one rename moved, at every venue in the org. */
export interface PrepTaskMove extends PrepTabRename {
  readonly moved: number;
}

export interface VenueBody {
  readonly name: string;
  readonly suburb: string;
  readonly state: string;
  readonly seats: number;
  /** Show prices on the guest page (#126). Left out, it is kept. */
  readonly guestPrices?: boolean;
  /** The venue's clock. Each left out is kept. */
  readonly timezone?: string;
  readonly rolloverHour?: number;
  readonly weekStartsOn?: number;
  /** The gross profit target, percent. Left out, it is kept. */
  readonly gpTarget?: number;
}

export interface InviteBody {
  readonly email: string;
  readonly displayName: string;
  readonly orgRole: string;
  readonly venues: readonly { readonly venueId: string; readonly venueRole: string }[];
}

// ------------------------------------------------------------------- calls

export const orgApi = {
  overview: (token: string) => request<OrgOverview>(token, "GET", "/v1/org"),

  saveSettings: (token: string, body: SettingsBody) =>
    request<{ settings: OrgSettings; clampedToPlan: boolean; prepTaskMoves: readonly PrepTaskMove[] }>(
      token,
      "PUT",
      "/v1/org/settings",
      body,
    ),

  createVenue: (token: string, body: VenueBody) =>
    request<{ venue: OrgVenue }>(token, "POST", "/v1/org/venues", body),

  saveVenue: (token: string, venueId: string, body: VenueBody) =>
    request<{ venue: OrgVenue; nightsRetotalled?: number }>(token, "PUT", `/v1/org/venues/${id(venueId)}`, body),

  invite: (token: string, body: InviteBody) =>
    request<{ member: OrgMember; invitationSent: boolean }>(
      token,
      "POST",
      "/v1/org/members",
      body,
    ),

  setOrgRole: (token: string, userId: string, orgRole: string) =>
    request<{ userId: string; orgRole: string }>(
      token,
      "PUT",
      `/v1/org/members/${id(userId)}`,
      { orgRole },
    ),

  assign: (token: string, userId: string, venueId: string, venueRole: string) =>
    request<{ userId: string; venueId: string; venueRole: string }>(
      token,
      "PUT",
      `/v1/org/members/${id(userId)}/venues/${id(venueId)}`,
      { venueRole },
    ),

  unassign: (token: string, userId: string, venueId: string) =>
    request<{ removed: boolean }>(
      token,
      "DELETE",
      `/v1/org/members/${id(userId)}/venues/${id(venueId)}`,
    ),

  removeMember: (token: string, userId: string) =>
    request<{ removed: boolean; note: string }>(
      token,
      "DELETE",
      `/v1/org/members/${id(userId)}`,
    ),
};
